import { type FC } from "react";

import { MetricCard } from "~/components/UI/MetricCard";
import { EventMetricsSkeleton } from "~/feature/event/components/EventMetrics/EventMetricsSkeleton";
import { prepareEventMetrics } from "~/feature/event/components/EventMetrics/prepareEventMetrics";
import { useEventMetrics } from "~/feature/event/components/EventMetrics/useEventMetrics";
import type { IAggregatedEvent } from "~/types/event";

import * as Styled from "./EventMetrics.styles";

interface IEventMetricsProps {
  event: IAggregatedEvent;
}

const INFO_TOOLTIP_TEXT =
  "This event represents an identical market listed across multiple prediction platforms. The values shown (Volume, Volume 24h, and Liquidity etc.) reflect aggregated data from all supported prediction markets, rather than a single marketplace.";

export const EventMetrics: FC<IEventMetricsProps> = ({ event }) => {
  const { data, isLoading, isChangeUnavailable } = useEventMetrics({
    id: event.id,
  });
  const showInfo = event.eventCount > 1;

  const metrics = prepareEventMetrics({ data, isChangeUnavailable });

  if (isLoading) {
    return <EventMetricsSkeleton />;
  }

  if (!metrics) {
    return null;
  }

  if (event.resolved) {
    return (
      <Styled.MetricSingleCard
        $isInfo={showInfo}
        title="Volume:"
        value={metrics.totalVolume.value}
        showInfo={showInfo}
        infoText={INFO_TOOLTIP_TEXT}
      />
    );
  }

  return (
    <Styled.MetricsContainer>
      <MetricCard
        title="Total volume:"
        value={metrics.totalVolume.value}
        showInfo={showInfo}
        infoText={INFO_TOOLTIP_TEXT}
      />
      <MetricCard
        title="Volume 24h:"
        value={metrics.volume24.value}
        percentage={metrics.volume24.percentage}
        showInfo={showInfo}
        infoText={INFO_TOOLTIP_TEXT}
      />
      <MetricCard
        title="Liquidity:"
        value={metrics.liquidity.value}
        percentage={metrics.liquidity.percentage}
        showInfo={showInfo}
        infoText={INFO_TOOLTIP_TEXT}
      />
      <MetricCard
        title="Open interest:"
        value={metrics.openInterest.value}
        percentage={metrics.openInterest.percentage}
        showInfo={showInfo}
        infoText={INFO_TOOLTIP_TEXT}
      />
    </Styled.MetricsContainer>
  );
};
